import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Layout from "../../../../components/template/Layout";
import { MediaItem } from "../../../../types/item";
import MediaViewer from "./mediaViewer";
import MediaThumbnail from "../media/MediaThumbnail";
import { AppDispatch } from "../../../../redux/store";
import {
  updateFormField,
  getFormData,
} from "../../../../redux/slices/inboundItemFormSlice";
import { InboundItem } from "../../../../types/item";
import { deleteImage } from "../../../../db";
import ConfirmModal from "../../../../components/template/ConfirmModal/ConfirmModal";
// import { FaTimesCircle, FaTrash } from "react-icons/fa";
import { FiTrash2 } from "react-icons/fi";

const ImageViewerPage: React.FC = () => {
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();

  // Get media from Redux (single source of truth)
  const formData = useSelector(getFormData) as InboundItem;
  // Filter out media marked for deletion
  const allMedia = formData?.media || location.state?.media || [];
  const images = allMedia.filter((media: MediaItem) => !media.toDelete);

  const itemTitle = location.state?.itemTitle || "Media";
  const initialMedia_ID = location.state?.initialMedia_ID || 0;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [mediaToDelete, setMediaToDelete] = useState<MediaItem | null>(null);

  useEffect(() => {
    // Only set initial index if provided, don't auto-open fullscreen
    if (initialMedia_ID && images.length > 0) {
      const index = images.findIndex(
        (image: MediaItem) => image.id === initialMedia_ID
      );
      if (index >= 0) {
        setCurrentIndex(index);
      }
    }
  }, []);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isFullscreen) return; // Only handle keyboard events in fullscreen mode

      switch (e.key) {
        case "ArrowLeft":
          prevImage();
          break;
        case "ArrowRight":
          nextImage();
          break;
        case "Escape":
          closeFullscreen();
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isFullscreen]);

  const nextImage = () => {
    setCurrentIndex((prev: number) => (prev + 1) % images.filter((media: MediaItem) => !media.toDelete).length);
  };

  const prevImage = () => {
    setCurrentIndex(
      (prev: number) => (prev - 1 + images.length) % images.length
    );
  };

  const openFullscreen = (index: number) => {
    setCurrentIndex(index);
    setIsFullscreen(true);
  };

  const closeFullscreen = () => {
    setIsFullscreen(false);
  };

  const requestDeleteMedia = (mediaItem: MediaItem) => {
    setMediaToDelete(mediaItem);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteMedia = async () => {
    if (!mediaToDelete) return;

    // Close modal first
    setShowDeleteConfirm(false);
    setMediaToDelete(null);

    // Check if media has been uploaded to server (has server ID)
    const mediaHasServerId = mediaToDelete.id && Number(mediaToDelete.id) > 0;
    
    // Check if the item itself has been saved to server
    const itemHasServerId = formData?.id && Number(formData.id) > 0;
    
    let updatedMedia: MediaItem[];

    if (mediaHasServerId || itemHasServerId) {
      // Media has server ID OR item has server ID: Mark for deletion instead of removing it
      // Server deletion will happen when user saves changes
      // NOTE: Do NOT delete from IndexedDB here - it will be deleted after successful server deletion
      // (handled in service worker after save is successful)
      // This also handles pending items that haven't been saved yet - preserves media until save
      updatedMedia = allMedia.map((media: MediaItem) =>
        (media.id === mediaToDelete.id || media.uuid && media.uuid === mediaToDelete.uuid)
          ? { ...media, toDelete: true }
          : media
      );
      dispatch(updateFormField({ field: "media", value: updatedMedia }));
    } else {
      // Media has no server ID AND item has no server ID: Safe to delete immediately
      // This is a brand new item that hasn't been saved yet, and media hasn't been uploaded
      // Delete blob from IndexedDB if it exists
      if (mediaToDelete?.uuid) {
        try {
          await deleteImage(mediaToDelete.uuid);
          console.log(`Deleted blob from IndexedDB: ${mediaToDelete.uuid}`);
        } catch (error) {
          console.warn(`Failed to delete blob ${mediaToDelete.uuid}:`, error);
        }
      }
      
      // Remove from array (truly new item - no server ID for item or media)
      updatedMedia = allMedia.filter((m: MediaItem) => {
        const isDifferentItem =
          m.id !== mediaToDelete.id && m.uuid !== mediaToDelete.uuid;
        return isDifferentItem;
      });
      dispatch(updateFormField({ field: "media", value: updatedMedia }));
    }

    // Filter out deleted items for display count
    const remainingMedia = updatedMedia.filter((media: MediaItem) => !media.toDelete);

    // If no more media, go back
    if (remainingMedia.length === 0) {
      window.history.back();
      return;
    }

    const wasLastItem = currentIndex === images.length - 1;
    if (wasLastItem && remainingMedia.length > 0) {
      setCurrentIndex(0);
    }
    // If we deleted a different item and the index is now out of bounds, adjust
    else if (currentIndex >= remainingMedia.length && remainingMedia.length > 0) {
      setCurrentIndex(remainingMedia.length - 1);
    }
  };

  const cancelDeleteMedia = () => {
    setShowDeleteConfirm(false);
    setMediaToDelete(null);
  };

  if (images.length === 0) {
    return (
      <Layout title="No Media" showBackIcon>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500">No media available</p>
        </div>
      </Layout>
    );
  }

  // Grid View
  if (!isFullscreen) {
    return (
      <Layout title={itemTitle} showBackIcon>
        <div className="p-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-1">
            {images.map((mediaItem: MediaItem, index: number) => (
              <div
                key={mediaItem.id || index}
                className="relative aspect-square rounded-sm overflow-hidden border border-gray-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer group"
                onClick={() => openFullscreen(index)}
              >
                <MediaThumbnail
                  mediaItem={mediaItem}
                  onError={() => {}}
                  className="w-full h-full object-cover"
                />
                {/* Delete button - only show on hover */}
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    requestDeleteMedia(mediaItem);
                  }}
                  className="absolute top-1 right-1 rounded-full bg-gray-500 text-white p-3 opacity-0 group-hover:opacity-80 transition-opacity duration-200 hover:bg-gray-800"
                >
                  <FiTrash2 className="w-5 h-5 text-white" />
                </button>
              </div>
            ))}
          </div>
          <p className="text-sm text-gray-500 mt-4 text-center">
            {images.length} media file{images.length !== 1 ? "s" : ""}
          </p>
        </div>

        {/* Delete confirmation modal for grid view */}
        <ConfirmModal
          isOpen={showDeleteConfirm}
          title="Delete Media"
          message="Are you sure you want to delete this media? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={confirmDeleteMedia}
          onCancel={cancelDeleteMedia}
        />
      </Layout>
    );
  }

  return (
    <Layout
      title={`${itemTitle} - ${currentIndex + 1} of ${images.length}`}
      showBackIcon
    >
      <MediaViewer
        media={images}
        currentIndex={currentIndex}
        onClose={closeFullscreen}
        onNext={nextImage}
        onPrev={prevImage}
        onViewGallery={() => {}} // No-op since we're already in gallery
        onDelete={() => requestDeleteMedia(images[currentIndex])}
        className="z-50"
        showGalleryButton={false}
      />

      <ConfirmModal
        isOpen={showDeleteConfirm}
        title="Delete Media"
        message="Are you sure you want to delete this media? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDeleteMedia}
        onCancel={cancelDeleteMedia}
      />
    </Layout>
  );
};

export default ImageViewerPage;
